import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { BiSolidStar, BiStar } from 'react-icons/bi';
import MainLayOut from '../../layout/MainLayOut';

const API = process.env.REACT_APP_API_URL;

const RATING_LABELS = ['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'];

function StarPicker({ value, onChange, disabled }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex items-center gap-2">
      {[1, 2, 3, 4, 5].map((s) => (
        <button
          key={s}
          type="button"
          disabled={disabled}
          onClick={() => !disabled && onChange(s)}
          onMouseEnter={() => !disabled && setHovered(s)}
          onMouseLeave={() => !disabled && setHovered(0)}
          className="focus:outline-none disabled:cursor-default text-3xl"
        >
          {s <= (hovered || value) ? (
            <BiSolidStar className="text-yellow-400" />
          ) : (
            <BiStar className="text-gray-300" />
          )}
        </button>
      ))}
    </div>
  );
}

const ReviewSubmission = () => {
  const { token } = useParams();

  const [info, setInfo] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [infoLoading, setInfoLoading] = useState(true);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) return;
    setInfoLoading(true);
    axios.get(`${API}/api/v1/review_link/${token}`)
      .then((res) => {
        if (res.data?.status) {
          setInfo(res.data);
          setName(res.data.customer_name || '');
          setEmail(res.data.customer_email || '');
        } else {
          setLoadError(res.data?.message || 'Invalid review link.');
        }
      })
      .catch(() => setLoadError('Unable to load review. The link may be invalid or expired.'))
      .finally(() => setInfoLoading(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error('Please enter your name.');
    if (!email.trim()) return toast.error('Please enter your email.');
    if (rating === 0) return toast.error('Please select a star rating.');
    if (!reviewText.trim()) return toast.error('Please write a short review.');

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/v1/review_link/${token}`, {
        name, email, rating, review_text: reviewText,
      });
      if (res.data?.status) {
        setDone(true);
      } else {
        toast.error(res.data?.message || 'Submission failed. Please try again.');
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
    }
    setSubmitting(false);
  };

  return (
    <MainLayOut>
      <div className="min-h-[70vh] bg-gradient-to-br from-gray-50 to-blue-50 flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <p className="text-xl font-bold text-gray-800 tracking-tight">Share Your Experience</p>
          </div>

          <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
            {infoLoading && (
              <div className="p-10 text-center">
                <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-sm text-gray-400">Loading...</p>
              </div>
            )}

            {!infoLoading && loadError && (
              <div className="p-10 text-center">
                <p className="font-semibold text-gray-800 mb-1">Link Unavailable</p>
                <p className="text-sm text-gray-400">{loadError}</p>
              </div>
            )}

            {done && (
              <div className="p-10 text-center">
                <p className="font-bold text-gray-800 text-lg mb-2">Thank you!</p>
                <p className="text-sm text-gray-500">Your review has been submitted and will appear after our team reviews it.</p>
              </div>
            )}

            {!infoLoading && !loadError && !done && info && (
              <>
                <div className="bg-gradient-to-r from-blue-500 to-blue-700 px-6 py-5 text-white">
                  <p className="text-xs font-semibold uppercase tracking-wider opacity-70 mb-1">You purchased</p>
                  <p className="font-bold text-lg leading-tight">{info.product_name}</p>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-5">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Your Name</label>
                    <input value={name} onChange={(e) => setName(e.target.value)}
                      placeholder="Enter your name"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-blue-500 transition-colors" />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Email Address</label>
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-blue-500 transition-colors" />
                    <p className="text-xs text-gray-400 mt-1">Must match the email used for your order.</p>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2.5">Your Rating</label>
                    <StarPicker value={rating} onChange={setRating} disabled={submitting} />
                    {rating > 0 && (
                      <p className="text-sm text-yellow-500 font-semibold mt-1.5">{RATING_LABELS[rating]}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Your Review</label>
                    <textarea value={reviewText} onChange={(e) => setReviewText(e.target.value)}
                      rows={4} placeholder="Tell others about your experience with this product..."
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-blue-500 transition-colors resize-none" />
                  </div>

                  <button type="submit" disabled={submitting || rating === 0}
                    className="w-full py-3.5 bg-blue-500 hover:bg-blue-700 disabled:opacity-50 text-white font-bold rounded-xl transition-colors text-sm">
                    {submitting ? 'Submitting...' : 'Submit Review'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </MainLayOut>
  );
};

export default ReviewSubmission;
